import React, { useState, useEffect } from 'react';

import { areCookiesEnabled } from '@repo/detection';
import { useTranslation } from '@repo/i18n';
import {
  onboardingStoreSelector,
  useOnboardingStore,
} from '@repo/onboarding/onboarding-store';
import { themeStyle } from '@repo/ui/theming';

import AnnouncementModal from 'components/AnnouncementModal/AnnouncementModal';
import Banner from 'components/Banner/Banner';
import Header from 'components/HeadMenu/Header/Header';
import useForcedAuthentication from 'components/hooks/useForcedAuthentication';
import { Wrapper, ContentWrapper } from 'components/Layout/styles';
import MobileFooter from 'components/MobileFooter/MobileFooter';
import NonFeedFooter from 'components/NonFeedFooter/NonFeedFooter';
import SmallFooter from 'components/SmallFooter/SmallFooter';
import { Container } from 'components/utilities/pageStyles';
import { PageBlocker, Message } from 'components/utilities/styles';
import { isInWebsiteOnboarding } from 'global/onboardingUtils';
import useWindowSize from 'global/utils/useWindowSize';
import {
  announcementStoreSelector,
  useAnnouncementStore,
} from 'stores/announcementStore/announcementStore';
import { bannerStoreSelector, useBannerStore } from 'stores/bannerStore';

import { useEditorAndDashboardUpdateFeature } from '../../global/growthbookUtils';

import { LayoutProps } from './types';

const LayoutFooter = ({
  customFooter,
  footerStyle,
  newBackground,
}: Pick<
  LayoutProps,
  'customFooter' | 'footerStyle' | 'newBackground'
>): React.ReactElement => {
  const { isMobile } = useWindowSize();

  if (customFooter) {
    const CustomFooter = customFooter;
    return <CustomFooter />;
  }

  if (footerStyle !== 'small') {
    return <NonFeedFooter newBackground={newBackground} />;
  }

  if (isMobile) {
    return (
      <MobileFooter theme={{ borderColor: themeStyle.varInkSuperLight }} />
    );
  }

  return <SmallFooter theme={{ borderColor: themeStyle.varInkSuperLight }} />;
};

/**
 * The Layout can be used to create a page.
 */
const Layout: React.FC<LayoutProps> = ({
  forceAuthentication = false,
  allowAnnouncement = true,
  ...props
}) => {
  const { t } = useTranslation();

  const [cookiesEnabled, setCookiesEnabled] = useState(true);

  const isAnnouncementModalOpen = useAnnouncementStore(
    announcementStoreSelector.isOpen
  );

  const hasBanner = useBannerStore(bannerStoreSelector.isVisible);
  const step = useOnboardingStore(onboardingStoreSelector.step);

  useEffect(() => {
    if (!areCookiesEnabled()) {
      setCookiesEnabled(false);
    }
  }, []);

  useForcedAuthentication({
    enabled: forceAuthentication && !isAnnouncementModalOpen,
    openWithoutDelay: props.openAuthWithoutDelay,
  });

  const isNewDesignEnabled = useEditorAndDashboardUpdateFeature();
  const newBackground = props.newBackground ?? isNewDesignEnabled;

  return (
    <Wrapper hasBanner={hasBanner} newBackground={newBackground}>
      {hasBanner && <Banner />}
      {!props.hideHeader && (
        <Header
          hasBanner={hasBanner}
          hiddenSections={props.headerHiddenSections}
          inviteReferral={props.inviteReferral}
          onHome={props.onHome}
          onSignup={props.onSignup}
        />
      )}

      <ContentWrapper
        forceDarkModeContent={!!props.forceDarkModeContent}
        hideHeader={props.hideHeader}
      >
        {forceAuthentication && !cookiesEnabled ? (
          <Container>
            <PageBlocker>
              <Message>{t('enable_cookies')}</Message>
            </PageBlocker>
          </Container>
        ) : (
          props.children
        )}
      </ContentWrapper>
      <LayoutFooter
        customFooter={props.customFooter}
        footerStyle={props.footerStyle}
        newBackground={newBackground}
      />
      {allowAnnouncement && !isInWebsiteOnboarding(step) && (
        <AnnouncementModal />
      )}
    </Wrapper>
  );
};

export default Layout;
